import {
  ArrowsClockwise,
  DotsThree,
  IdentificationCard,
  Plus,
  UsersThree,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ApiError } from "~/api/errors";
import type { AuthTeam } from "~/api/governanceTypes";
import {
  useAuthIdentity,
  useAuthMyTeams,
  useAuthTeamMembers,
  useAuthTeams,
  useCreateAuthTeam,
  useCurrentWorkspace,
  useDeleteAuthTeam,
  useSyncAuthIdentity,
  useUpdateAuthTeam,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Row, Rows } from "~/features/automation/components";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

function TeamDialog({ team, onClose }: { team?: AuthTeam; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const createTeam = useCreateAuthTeam();
  const updateTeam = useUpdateAuthTeam();
  const [name, setName] = useState(team?.name ?? "");
  const [slug, setSlug] = useState(team?.slug ?? "");
  const isEdit = Boolean(team);
  const busy = createTeam.isPending || updateTeam.isPending;

  const submit = () => {
    if (!name.trim() || !slug.trim()) {
      return;
    }
    if (isEdit && team) {
      updateTeam.mutate(
        { teamId: team.id, name: name.trim(), slug: slug.trim() },
        {
          onSuccess: () => {
            onClose();
            toast.success(t("Team saved"));
          },
        },
      );
    } else if (workspace) {
      createTeam.mutate(
        { name: name.trim(), slug: slug.trim(), organizationId: workspace.id },
        {
          onSuccess: () => {
            onClose();
            toast.success(t("Team created"));
          },
        },
      );
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isEdit ? t("Rename team") : t("New Docit.Auth team")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || !slug.trim() || busy}
            onClick={submit}
          >
            {isEdit ? t("Save") : t("Create")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t("Name")}
          fullWidth
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label={t("Slug")}
          fullWidth
          value={slug}
          onChange={(event) => setSlug(event.target.value)}
        />
      </div>
    </Dialog>
  );
}

function TeamMembersDialog({ team, onClose }: { team: AuthTeam; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: members, isLoading, isError } = useAuthTeamMembers(team.id);

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={team.name}
      closeLabel={t("Close")}
      size="md"
    >
      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : isError ? (
        <Empty message={t("This team is not in your organisation.")} />
      ) : !members?.length ? (
        <Empty message={t("No members yet")} />
      ) : (
        <Rows>
          {members.map((member) => (
            <Row key={member.userId}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">{member.name ?? member.userId}</p>
                <p className="truncate text-small text-text-tertiary">{member.email}</p>
              </div>
              <span className="text-small text-text-secondary">{member.role}</span>
              <span className="text-small text-text-tertiary">{member.status}</span>
            </Row>
          ))}
        </Rows>
      )}
    </Dialog>
  );
}

function TeamsBlock() {
  const { t } = useTranslation();
  const { data: teams, isLoading } = useAuthTeams();
  const deleteTeam = useDeleteAuthTeam();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AuthTeam | null>(null);
  const [viewing, setViewing] = useState<AuthTeam | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AuthTeam | null>(null);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Text variant="small" className="font-medium text-text-secondary">
          {t("Teams")}
        </Text>
        <Button size="sm" variant="neutral" icon={Plus} onClick={() => setCreating(true)}>
          {t("New team")}
        </Button>
      </div>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : !teams?.length ? (
        <Empty icon={UsersThree} message={t("No teams yet")} />
      ) : (
        <>
          <Rows>
            {teams.map((team) => (
              <Row key={team.id}>
                <UsersThree size={18} aria-hidden className="shrink-0 text-text-tertiary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body text-text">{team.name}</p>
                  <p className="truncate font-mono text-small text-text-tertiary">{team.slug}</p>
                </div>
                <Menu
                  items={
                    [
                      { type: "button", title: t("Members…"), onClick: () => setViewing(team) },
                      { type: "button", title: t("Rename…"), onClick: () => setEditing(team) },
                      { type: "separator" },
                      {
                        type: "button",
                        title: t("Delete…"),
                        dangerous: true,
                        onClick: () => setConfirmDelete(team),
                      },
                    ] satisfies MenuItem[]
                  }
                  trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} size="sm" />}
                />
              </Row>
            ))}
          </Rows>
          <p className="text-small text-text-tertiary">
            {t("Showing the first 50 teams. Paging through Docit.Auth teams needs a proxy change.")}
          </p>
        </>
      )}

      {creating ? <TeamDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <TeamDialog team={editing} onClose={() => setEditing(null)} /> : null}
      {viewing ? <TeamMembersDialog team={viewing} onClose={() => setViewing(null)} /> : null}
      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t("Delete {{name}}?", { name: confirmDelete?.name ?? "" })}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={deleteTeam.isPending}
        onConfirm={() => {
          if (!confirmDelete) {
            return;
          }
          deleteTeam.mutate(confirmDelete.id, {
            onSuccess: () => {
              setConfirmDelete(null);
              toast.success(t("Team deleted"));
            },
          });
        }}
      />
    </div>
  );
}

/**
 * §A.1.1/§A.1.2: identity + sync + the Auth teams block, folded into one section — same
 * service, same gate, one screen of content. `sync()` is a WRITE (§E-3): it never fires on
 * mount, only from the explicit button below.
 */
export function DocitAuthSection() {
  const { t } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const { data: identity } = useAuthIdentity();
  const { data: myTeams } = useAuthMyTeams();
  const syncIdentity = useSyncAuthIdentity();
  const [notConfigured, setNotConfigured] = useState(false);

  const canAdminister = workspace?.abilities[ABILITY.workspaceUpdate] === true;

  if (!canAdminister) {
    return (
      <Empty
        icon={IdentificationCard}
        message={t("Docit.Auth administration is limited to workspace admins")}
      />
    );
  }

  const sync = () => {
    syncIdentity.mutate(undefined, {
      onError: (error) => {
        if (error instanceof ApiError && error.status === 500) {
          setNotConfigured(true);
        }
      },
    });
  };

  if (notConfigured) {
    return (
      <Empty
        icon={IdentificationCard}
        message={t("Docit.Auth is not configured for this deployment")}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 rounded-md border border-divider p-4">
        <div className="flex items-center justify-between">
          <Text variant="small" className="font-medium text-text-secondary">
            {t("Identity")}
          </Text>
          <Button
            size="sm"
            variant="neutral"
            icon={ArrowsClockwise}
            disabled={syncIdentity.isPending}
            onClick={sync}
          >
            {syncIdentity.isPending ? t("Syncing…") : t("Sync now")}
          </Button>
        </div>

        {identity ? (
          <div className="flex flex-col gap-1 text-body text-text">
            <p>{identity.email}</p>
            <p className="text-small text-text-tertiary">{identity.userId}</p>
          </div>
        ) : (
          <p className="text-small text-text-tertiary">{t("Not synced yet.")}</p>
        )}

        {myTeams?.length ? (
          <ul className="flex flex-col gap-1">
            {myTeams.map((team) => (
              <li key={team.teamId} className="text-small text-text-secondary">
                {team.name ?? team.teamId} · {team.role} · {team.status}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <TeamsBlock />
    </div>
  );
}
